import React, { useState, useMemo, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Swal from 'sweetalert2';

const DataTable = ({ data: initialData, endpoint, onDataChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(initialData || []);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeyLoading, setForeignKeyLoading] = useState(false);
  const [foreignKeyErrors, setForeignKeyErrors] = useState({});
  const [displayColumns, setDisplayColumns] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageSizeOptions = [10, 30, 50, { value: 'all', label: 'All' }];

  // List of boolean columns that need Yes/No display
  const booleanColumns = ['is_active', 'is_test', 'is_processed', 'is_deleted'];
  
  // List of read-only columns (timestamps)
  const readOnlyColumns = ['id', 'file_uuid', 'created_at', 'updated_at', 'token', 'reset_password'];

  // Memoize the hiddenColumns array so it doesn't change between renders
  const hiddenColumns = useMemo(() => [
    'id', 
    'file_uuid', 
    'token', 
    'reset_password'
  ], []);

  // Weekday options for day_of_week column
  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

  // Convert boolean value to Yes/No for display
  const displayBooleanValue = (value) => {
    if (value === true || value === 1 || value === '1') return 'Yes';
    if (value === false || value === 0 || value === '0') return 'No';
    return value; // Return as-is if not a boolean value
  };

  // Convert Yes/No back to 1/0 for submission
  const parseBooleanInput = (value) => {
    if (value === 'Yes') return 1;
    if (value === 'No') return 0;
    return value; // Return as-is if not Yes/No
  };

  // Get the name for a foreign key value
  const getForeignKeyName = (column, id) => {
    if (!foreignKeyData[column] || !id) return id;
    const relatedItem = foreignKeyData[column].find(item => item.id === id); // Loose comparison
    return relatedItem ? (relatedItem.name || relatedItem.title || relatedItem.label || id) : id;
  };

  // Sync with parent data changes
  useEffect(() => {
    setData(initialData || []);
    setCurrentPage(1); // Reset to first page when data changes
  }, [initialData]);

  // Set display columns (excluding hidden columns)
  useEffect(() => {
    if (data.length > 0) {
      const allColumns = Object.keys(data[0]);
      setDisplayColumns(allColumns.filter(col => !hiddenColumns.includes(col)));
    }
  }, [data, hiddenColumns]);

  // Identify foreign key columns and fetch their data
  useEffect(() => {
    if (data.length === 0) return;

    const fkColumns = Object.keys(data[0])
      .filter(col => col.endsWith('_id') && col !== 'id')
      .filter(col => !(col in foreignKeyData)); // Only fetch if we haven't already

    if (fkColumns.length === 0) return;

    const fetchForeignKeyData = async () => {
      setForeignKeyLoading(true);
      const newFkData = {};
      const newErrors = {};
      
      for (const col of fkColumns) {
        // Handle pluralization (e.g., company_id -> companies)
        let relatedEndpoint = col.replace(/_id$/, '');
        
        // Handle special cases first
        if (relatedEndpoint === 'address') {
          relatedEndpoint = 'addresses';
        } 
        // Then handle common irregular plurals
        else if (relatedEndpoint.endsWith('y')) {
          relatedEndpoint = relatedEndpoint.slice(0, -1) + 'ies';
        } 
        // Default case - add 's'
        else {
          relatedEndpoint = relatedEndpoint + 's';
        }

        try {
          const response = await apiClient.get(`api/${relatedEndpoint}/v1`);
          newFkData[col] = response.data;
        } catch (error) {
          console.error(`Error fetching data for ${relatedEndpoint}:`, error);
          newErrors[col] = `Could not load ${relatedEndpoint}`;
          newFkData[col] = [];
        }
      }
      
      setForeignKeyData(prev => ({ ...prev, ...newFkData }));
      setForeignKeyErrors(prev => ({ ...prev, ...newErrors }));
      setForeignKeyLoading(false);
    };
    
    fetchForeignKeyData();
  }, [data, foreignKeyData]);

  const filteredData = data.filter((item) =>
    Object.entries(item).some(([key, value]) => {
      // Include the related name in search if this is a foreign key
      if (key.endsWith('_id') && foreignKeyData[key]) {
        const name = getForeignKeyName(key, value);
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return (
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
  );

  // Pagination logic
  const indexOfLastItem = itemsPerPage === 'all' ? filteredData.length : currentPage * itemsPerPage;
  const indexOfFirstItem = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const currentItems = itemsPerPage === 'all' 
    ? filteredData 
    : filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    // Convert boolean values to Yes/No for editing
    const editData = { ...item };
    booleanColumns.forEach(col => {
      if (col in editData) {
        editData[col] = displayBooleanValue(editData[col]);
      }
    });
    setFormData(editData);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let dataToSend;
      let response;
      
      if (editingId) {
        // Prepare data for update - convert Yes/No back to 1/0
        dataToSend = { ...formData };
        booleanColumns.forEach(col => {
          if (col in dataToSend) {
            dataToSend[col] = parseBooleanInput(dataToSend[col]);
          }
        });
        
        response = await apiClient.put(`api/${endpoint}/v1/${editingId}`, dataToSend);
        setData(data.map(item => item.id === editingId ? response.data : item));
        onDataChange?.();
      } else if (isAdding) {
        // Prepare data for create - convert Yes/No back to 1/0
        dataToSend = { ...newItem };
        booleanColumns.forEach(col => {
          if (col in dataToSend) {
            dataToSend[col] = parseBooleanInput(dataToSend[col]);
          }
        });
        
        // Remove read-only columns from the data being sent
        readOnlyColumns.forEach(col => {
          delete dataToSend[col];
        });
        
        response = await apiClient.post(`api/${endpoint}/v1`, dataToSend);
        setData([...data, response.data]);
        onDataChange?.();
      }

      // Show SweetAlert if there's a message in the response
      if (response?.data?.message) {
        await Swal.fire({
          title: 'Success',
          text: response.data.message,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Reload the table after the alert is closed
        try {
          const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
          setData(refreshResponse.data);
          onDataChange?.();
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
        }
      }
    } catch (error) {
      console.error('Error saving data:', error);
      // Show error message if available
      if (error.response?.data?.message) {
        await Swal.fire({
          title: 'Error',
          text: error.response.data.message,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        await Swal.fire({
          title: 'Error',
          text: 'An error occurred while saving the data',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setIsLoading(false);
      setEditingId(null);
      setIsAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setIsLoading(true);
      try {
        const response = await apiClient.delete(`api/${endpoint}/v1/${id}`);
        setData(data.filter(item => item.id !== id));
        onDataChange?.();

        // Show success message if available
        if (response?.data?.message) {
          await Swal.fire({
            title: 'Success',
            text: response.data.message,
            icon: 'success',
            confirmButtonText: 'OK'
          });

          // Reload the table after the alert is closed
          try {
            const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
            setData(refreshResponse.data);
            onDataChange?.();
          } catch (refreshError) {
            console.error('Error refreshing data:', refreshError);
          }
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        // Show error message if available
        if (error.response?.data?.message) {
          await Swal.fire({
            title: 'Error',
            text: error.response.data.message,
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (editingId) {
      setFormData({ ...formData, [name]: value });
    } else if (isAdding) {
      setNewItem({ ...newItem, [name]: value });
    }
  };

  const handleAddNew = () => {
    setIsAdding(true);
    // Initialize newItem with empty values for all columns
    if (data.length > 0) {
      const emptyItem = {};
      Object.keys(data[0]).forEach(key => {
        // Initialize boolean columns with 'No' (0)
        emptyItem[key] = booleanColumns.includes(key) ? 'No' : '';
        // Don't initialize read-only columns
        if (readOnlyColumns.includes(key)) {
          delete emptyItem[key];
        }
      });
      setNewItem(emptyItem);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="data-table">
        <button className="btn-add" onClick={handleAddNew}>
          Add New
        </button>
        <div>No data available</div>
      </div>
    );
  }

  const allColumns = Object.keys(data[0]).filter(col => 
    !(isAdding && readOnlyColumns.includes(col)) &&
    !hiddenColumns.includes(col)
  );

  return (
    <div className="data-table">
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="btn-add" onClick={handleAddNew}>
          Add New
        </button>
      </div>
      
      {/* Items per page selector */}
      <div className="pagination-controls">
        <div className="items-per-page">
          <label>Items per page:</label>
          <select 
            value={itemsPerPage} 
            onChange={handleItemsPerPageChange}
            disabled={isLoading}
          >
            {pageSizeOptions.map(option => (
              <option key={option.value || option} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
        </div>
        
        {/* Pagination navigation */}
        {itemsPerPage !== 'all' && (
          <div className="pagination-navigation">
            <button 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1 || isLoading}
            >
              First
            </button>
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Next
            </button>
            <button 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Last
            </button>
          </div>
        )}
      </div>
      
      {isAdding && (
        <div className="add-form">
          <h3>Add New Item</h3>
          <div className="form-fields">
            {allColumns.map((col) => (
              <div key={`new-${col}`} className="form-field">
                <label>{col.replace(/_/g, ' ')}:</label>
                {booleanColumns.includes(col) ? (
                  <select
                    name={col}
                    value={newItem[col] || 'No'}
                    onChange={handleChange}
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : col === 'day_of_week' ? (
                  <select
                    name={col}
                    value={newItem[col] || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select Day</option>
                    {weekdays.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                ) : col.endsWith('_id') && foreignKeyData[col] ? (
                  <>
                    {foreignKeyErrors[col] ? (
                      <div className="error-message">{foreignKeyErrors[col]}</div>
                    ) : (
                      <select
                        name={col}
                        value={newItem[col] || ''}
                        onChange={handleChange}
                        disabled={foreignKeyLoading}
                      >
                        <option value="">Select {col.replace('_id', '').replace(/_/g, ' ')}</option>
                        {foreignKeyData[col].map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name || item.title || item.label || item.id}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    name={col}
                    value={newItem[col] || ''}
                    onChange={handleChange}
                    disabled={col === 'id' || readOnlyColumns.includes(col)}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handleCancel} disabled={isLoading}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <table>
        <thead>
          <tr>
            {displayColumns.map((col) => (
              <th key={col}>
                {col.endsWith('_id') 
                  ? col.replace('_id', '').replace(/_/g, ' ')
                  : col.replace(/_/g, ' ')}
              </th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((item) => (
            <tr key={item.id}>
              {displayColumns.map((col) => (
                <td key={`${item.id}-${col}`}>
                  {editingId === item.id ? (
                    readOnlyColumns.includes(col) ? (
                      // Display read-only fields without editing capability
                      item[col]
                    ) : booleanColumns.includes(col) ? (
                      <select
                        name={col}
                        value={formData[col] || 'No'}
                        onChange={handleChange}
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    ) : col === 'day_of_week' ? (
                      <select
                        name={col}
                        value={formData[col] || ''}
                        onChange={handleChange}
                      >
                        <option value="">Select Day</option>
                        {weekdays.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    ) : col.endsWith('_id') && foreignKeyData[col] ? (
                      <>
                        {foreignKeyErrors[col] ? (
                          <div className="error-message">{foreignKeyErrors[col]}</div>
                        ) : (
                          <select
                            name={col}
                            value={formData[col] || ''}
                            onChange={handleChange}
                            disabled={foreignKeyLoading}
                          >
                            <option value="">Select {col.replace('_id', '').replace(/_/g, ' ')}</option>
                            {foreignKeyData[col].map(fkItem => (
                              <option key={fkItem.id} value={fkItem.id}>
                                {fkItem.name || fkItem.title || fkItem.label || fkItem.id}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    ) : (
                      <input
                        type="text"
                        name={col}
                        value={formData[col] || ''}
                        onChange={handleChange}
                        disabled={col === 'id' || readOnlyColumns.includes(col)}
                      />
                    )
                  ) : (
                    booleanColumns.includes(col) 
                      ? displayBooleanValue(item[col]) 
                      : col.endsWith('_id') 
                        ? getForeignKeyName(col, item[col]) 
                        : item[col]
                  )}
                </td>
              ))}
              <td>
                {editingId === item.id ? (
                  <>
                    <button onClick={handleSave} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={handleCancel} disabled={isLoading}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleEdit(item)} disabled={isLoading || isAdding}>
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(item.id)} 
                      disabled={isLoading || isAdding}
                    >
                      Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Pagination controls at the bottom */}
      {itemsPerPage !== 'all' && (
        <div className="pagination-controls bottom">
          <div className="pagination-navigation">
            <button 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1 || isLoading}
            >
              First
            </button>
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            
            <span className="page-info">
              Page {currentPage} of {totalPages} (Showing {currentItems.length} of {filteredData.length} items)
            </span>
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Next
            </button>
            <button 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;